import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useCompanies } from './hooks';
import { useCurrentDomain } from './context/DomainContext';
import {
    getKioskAllowedCompanies,
    addKioskAllowedCompany,
    updateKioskAllowedCompany,
    deleteKioskAllowedCompany,
} from './services/masterDataService';

const BRAND_GREEN = '#0aa375';

const Field = ({ label, required }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span style={{ fontSize: '10px', fontWeight: 600, color: 'black', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            {label}
            {required && <span style={{ color: '#dc3545', marginLeft: '3px', fontSize: '14px', lineHeight: '1' }}>*</span>}
        </span>
    </div>
);

const Spinner = () => (
    <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm" style={{ color: BRAND_GREEN }} role="status">
            <span className="visually-hidden">Loading...</span>
        </div>
    </div>
);

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Companies the Kiosk menu/feature is enabled for (dbo.mst_KioskAllowedCompany) — replaces
// the Doctor frontend's hardcoded KIOSK_ALLOWED_COMPANY_IDS array with a DB-driven list staff
// can manage here. "Delete" is a soft delete (Status=0, see deleteKioskAllowedCompany) — the
// row and its CompanyID are kept so re-enabling it later is just a status toggle.
const KioskAllowedCompanies = () => {
    const { domain } = useCurrentDomain();
    const { companies, loading: companiesLoading } = useCompanies(domain);

    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [companySearch, setCompanySearch] = useState('');
    const [rows, setRows] = useState([]);
    const [rowsLoading, setRowsLoading] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [search, setSearch] = useState('');

    const fetchRows = useCallback(async () => {
        if (!domain) {
            setRows([]);
            return;
        }
        setRowsLoading(true);
        try {
            const data = await getKioskAllowedCompanies(domain);
            setRows(data || []);
        } catch (err) {
            toast.error('Failed to load allowed companies.');
        } finally {
            setRowsLoading(false);
        }
    }, [domain]);

    useEffect(() => {
        fetchRows();
    }, [fetchRows]);

    const companyLabel = (companyId) => {
        const match = companies.find((c) => String(c.value) === String(companyId));
        return match ? match.label : companyId;
    };

    const handleAdd = async () => {
        if (selectedCompanies.length === 0) return;
        setAddLoading(true);
        const loadingToast = toast.loading('Adding companies...');
        try {
            const results = await Promise.allSettled(
                selectedCompanies.map((id) => addKioskAllowedCompany(id, domain))
            );
            const failed = results.filter((r) => r.status === 'rejected');
            const addedCount = results.length - failed.length;
            toast.dismiss(loadingToast);
            if (addedCount > 0) {
                toast.success(`${addedCount} ${addedCount === 1 ? 'company' : 'companies'} added to the allowed list.`);
            }
            if (failed.length > 0) {
                toast.error(failed[0].reason?.response?.data?.message || `Failed to add ${failed.length} ${failed.length === 1 ? 'company' : 'companies'}.`);
            }
            // Keep only the failed ones selected so they can be retried.
            setSelectedCompanies(selectedCompanies.filter((_, i) => results[i].status === 'rejected'));
            await fetchRows();
        } finally {
            setAddLoading(false);
        }
    };

    const handleToggleStatus = async (row) => {
        const enabling = !row.status;
        setBusyId(row.id);
        const loadingToast = toast.loading(enabling ? 'Enabling...' : 'Disabling...');
        try {
            if (enabling) {
                await updateKioskAllowedCompany(row.id, { status: true }, domain);
            } else {
                // Soft delete — sets Status=0 server-side, row and CompanyID are kept.
                await deleteKioskAllowedCompany(row.id, domain);
            }
            toast.dismiss(loadingToast);
            toast.success(enabling ? 'Company enabled.' : 'Company disabled.');
            await fetchRows();
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || 'Failed to update company.');
        } finally {
            setBusyId(null);
        }
    };

    const filteredRows = rows.filter((r) =>
        companyLabel(r.companyID).toLowerCase().includes(search.toLowerCase())
    );

    // Excludes companies already in the list regardless of status — a disabled row's correct
    // path back is "Enable" on that row (right panel), not a second Add (the backend rejects a
    // duplicate CompanyID either way, but steering here avoids a confusing round-trip error).
    const existingCompanyIds = new Set(rows.map((r) => String(r.companyID)));
    const addableCompanies = companies.filter((c) => !existingCompanyIds.has(String(c.value)));
    const filteredAddableCompanies = addableCompanies.filter((c) =>
        c.label.toLowerCase().includes(companySearch.toLowerCase())
    );

    return (
        <div className="container-fluid p-0 px-1">
            <div className="card-body">
                <div className="row g-2">
                    <div className="col-12 col-lg-5">
                        <div className="card border h-100" style={{ borderRadius: '6px' }}>
                            <div className="card-header" style={{ color: '#fff', fontWeight: 600, fontSize: 14, padding: '8px', backgroundColor: BRAND_GREEN }}>
                                Add Allowed Company
                            </div>
                            <div className="card-body p-2">
                                <div className="mb-2">
                                    <Field label="Search Companies" />
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Filter company list..."
                                        value={companySearch}
                                        onChange={(e) => setCompanySearch(e.target.value)}
                                        style={{ fontSize: '12px' }}
                                    />
                                </div>
                                <div style={{ height: '280px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', background: '#fafafa' }}>
                                    {companiesLoading ? (
                                        <Spinner />
                                    ) : filteredAddableCompanies.length === 0 ? (
                                        <div className="text-center py-4" style={{ color: '#adb5bd', fontSize: '12px' }}>
                                            No companies available.
                                        </div>
                                    ) : (
                                        filteredAddableCompanies.map((c) => (
                                            <label
                                                key={c.value}
                                                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '12px' }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedCompanies.includes(c.value)}
                                                    onChange={() =>
                                                        setSelectedCompanies((prev) =>
                                                            prev.includes(c.value)
                                                                ? prev.filter((id) => id !== c.value)
                                                                : [...prev, c.value]
                                                        )
                                                    }
                                                    style={{ accentColor: BRAND_GREEN }}
                                                />
                                                <div style={{ flex: 1, minWidth: 0, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {c.label}
                                                </div>
                                            </label>
                                        ))
                                    )}
                                </div>
                                <div style={{ fontSize: '11px', color: '#adb5bd', marginTop: '4px' }}>
                                    The Kiosk menu/feature will be enabled for the selected companies.
                                </div>
                                <div className="d-flex justify-content-center gap-2 mt-2">
                                    <button
                                        onClick={() => { setSelectedCompanies([]); setCompanySearch(''); }}
                                        className="btn btn-outline-secondary btn-sm px-3"
                                        style={{ fontSize: '12px' }}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        onClick={handleAdd}
                                        disabled={addLoading || selectedCompanies.length === 0}
                                        className="btn btn-sm text-white px-3 fw-semibold"
                                        style={{ backgroundColor: BRAND_GREEN, border: 'none', fontSize: '12px' }}
                                    >
                                        {addLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                Adding...
                                            </>
                                        ) : (
                                            `Add to Allowed List (${selectedCompanies.length})`
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-lg-7">
                        <div className="card border h-100" style={{ borderRadius: '6px' }}>
                            <div className="card-header" style={{ color: '#fff', fontWeight: 600, fontSize: 14, padding: '8px', backgroundColor: BRAND_GREEN }}>
                                Allowed Companies
                            </div>
                            <div className="card-body p-2">
                                <div className="mb-2">
                                    <Field label="Search" />
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Filter by company..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        style={{ fontSize: '12px' }}
                                    />
                                </div>
                                <div style={{ height: '360px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                                    {rowsLoading ? (
                                        <Spinner />
                                    ) : !domain ? (
                                        <div className="text-center py-4" style={{ color: '#adb5bd', fontSize: '12px' }}>
                                            Select a domain first.
                                        </div>
                                    ) : filteredRows.length === 0 ? (
                                        <div className="alert alert-success small py-1 mb-0 m-2">No allowed companies yet.</div>
                                    ) : (
                                        <table className="table table-sm table-hover mb-0" style={{ fontSize: '12px' }}>
                                            <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                                                <tr>
                                                    <th style={{ padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' }}>Company</th>
                                                    <th style={{ padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' }}>Status</th>
                                                    <th style={{ padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' }}>Added</th>
                                                    <th style={{ padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRows.map((row) => (
                                                    <tr key={row.id}>
                                                        <td style={{ padding: '6px 10px' }}>{companyLabel(row.companyID)}</td>
                                                        <td style={{ padding: '6px 10px' }}>
                                                            <span
                                                                style={{
                                                                    fontSize: '10px',
                                                                    fontWeight: 600,
                                                                    padding: '2px 8px',
                                                                    borderRadius: '20px',
                                                                    background: row.status ? '#e6f7f2' : '#f8d7da',
                                                                    color: row.status ? BRAND_GREEN : '#842029',
                                                                    border: `1px solid ${row.status ? BRAND_GREEN : '#842029'}22`,
                                                                }}
                                                            >
                                                                {row.status ? 'Enabled' : 'Disabled'}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '6px 10px', color: '#6c757d' }}>{formatDate(row.createdAt)}</td>
                                                        <td style={{ padding: '6px 10px' }}>
                                                            <button
                                                                onClick={() => handleToggleStatus(row)}
                                                                disabled={busyId === row.id}
                                                                className={`btn btn-sm px-2 ${row.status ? 'btn-outline-danger' : 'btn-outline-success'}`}
                                                                style={{ fontSize: '11px' }}
                                                            >
                                                                {busyId === row.id ? (
                                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                                                ) : row.status ? (
                                                                    'Disable'
                                                                ) : (
                                                                    'Enable'
                                                                )}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KioskAllowedCompanies;
